import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import api from "../../api";
import useStore from "../../store";
import {
  getPillarLibrary,
  savePillarLibrary,
  resetPillarLibrary,
} from "../../utils/pillarStorage";

export default function ManageUsersPage() {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [organization, setOrganization] = useState({ id: 0, name: "" });
  const [orgUsers, setOrgUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const username = useStore((store) => store.activeUser);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [fieldErrors, setFieldErrors] = useState({});
  const [addUserConfirmOpen, setAddUserConfirmOpen] = useState(false);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);

  // Default Pillars Management
  // {name, definition} rows. The definition is sent to the BE as `pillars_def`
  // and is what autoconfig uses to place each question, so it is not optional
  // decoration — a pillar without one gives the classifier only its name.
  const [defaultPillars, setDefaultPillars] = useState(getPillarLibrary);
  const [newPillarDefinition, setNewPillarDefinition] = useState("");
  const [editingPillarDefinition, setEditingPillarDefinition] = useState("");
  const [editingPillarIndex, setEditingPillarIndex] = useState(null);
  const [editingPillarValue, setEditingPillarValue] = useState("");
  const [newPillarValue, setNewPillarValue] = useState("");

  // Fetch organization and users
  const fetchProfile = useCallback(async () => {
    if (!username) {
      setError(intl.formatMessage({ id: "manage_users.no_username", defaultMessage: "No username provided. Please log in." }));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const profileRes = await api.getUserProfile(username);
      if (profileRes.status === 200) {
        setOrganization({
          id: profileRes.data.organization.id,
          name: profileRes.data.organization.name,
        });
      } else {
        throw new Error(intl.formatMessage({ id: "manage_users.fetch_profile_failed", defaultMessage: "Failed to fetch user profile" }));
      }

      const usersRes = await api.getOrganizationUsers();
      if (usersRes.status === 200) {
        setOrgUsers(usersRes.data);
      } else {
        throw new Error(intl.formatMessage({ id: "manage_users.fetch_org_users_failed", defaultMessage: "Failed to fetch organization users" }));
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching organization data:", error);
      setError(intl.formatMessage({ id: "manage_users.error_loading_data", defaultMessage: "Error loading organization data. Please try again." }));
      enqueueSnackbar(<FormattedMessage id="manage_users.fetch_failed" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, [username, enqueueSnackbar]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Handle input changes for new user form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
  const isValidUsername = (username) => /^[a-zA-Z0-9._]+$/.test((username || "").trim());

  // Open add-user confirmation modal (after form validation)
  const handleAddUserSubmit = (e) => {
    e.preventDefault();
    if (!newUser.first_name || !newUser.last_name || !newUser.username || !newUser.email) {
      enqueueSnackbar(<FormattedMessage id="manage_users.all_fields_required" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
      return;
    }
    if (!isValidUsername(newUser.username)) {
      enqueueSnackbar(<FormattedMessage id="manage_users.invalid_username" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
      return;
    }
    if (!isValidEmail(newUser.email)) {
      enqueueSnackbar(<FormattedMessage id="manage_users.invalid_email" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
      return;
    }
    setAddUserConfirmOpen(true);
  };

  // Add new user (called from confirmation modal or directly)
  const handleAddUser = async (e) => {
    if (e) e.preventDefault();

    if (!newUser.first_name || !newUser.last_name || !newUser.username || !newUser.email) {
      return;
    }

    setAddUserConfirmOpen(false);

    try {
      const response = await api.addOrganizationUser({
        ...newUser,
        organization_id: organization.id,
      });

      if (response.success) {
        // Handle success (status 201 or any success case)
        await fetchProfile();
        setNewUser({ first_name: "", last_name: "", username: "", email: "" });
        enqueueSnackbar(<FormattedMessage id="manage_users.user_added" />, {
          variant: "success",
          autoHideDuration: 2000,
        });
      } else {
        // Handle failure based on status and error details
        let errorMessage = <FormattedMessage id="manage_users.add_failed" />;
        let fieldErrors = {};

        console.log(response.fieldErrors)
        if (response.status === 400) {
          // Bad Request - likely validation errors
          if (response.fieldErrors?.username || response.fieldErrors?.email) {
            errorMessage = <FormattedMessage id="manage_users.user_exists" />;
            setFieldErrors({ currentPassword: errorMessage });
          } else {
            errorMessage = <FormattedMessage id="manage_users.invalid_request" />;
          }
        } else if (response.status === 401) {
          errorMessage = <FormattedMessage id="manage_users.unauthorized" />;
        } else if (response.status >= 500) {
          errorMessage = <FormattedMessage id="manage_users.server_error" />;
        } else if (response.error) {
          errorMessage = response.error;
        }

        enqueueSnackbar(errorMessage, { variant: "error", autoHideDuration: 2000 });
        setFieldErrors(fieldErrors); // Update field-specific errors in state if needed
      }
    } catch (err) {
      console.error("Failed to add user:", err);
      // Handle unexpected errors (e.g., network issues)
      enqueueSnackbar(<FormattedMessage id="manage_users.add_failed" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
  };

  // Remove users: pass an array of user ids to remove those, or no arg to remove selectedRows
  const handleRemoveUsers = async (userIds = null) => {
    const idsToRemove = userIds != null && userIds.length > 0 ? userIds : selectedRows;
    if (idsToRemove.length === 0) {
      enqueueSnackbar(<FormattedMessage id="manage_users.no_users_selected" />, {
        variant: "warning",
        autoHideDuration: 2000,
      });
      return;
    }

    try {
      setLoading(true);
      await api.removeOrganizationUsers(idsToRemove);
      await fetchProfile();
      setSelectedRows((prev) => prev.filter((id) => !idsToRemove.includes(id)));
      enqueueSnackbar(<FormattedMessage id="manage_users.users_removed" />, {
        variant: "success",
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error("Failed to remove users:", error);
      enqueueSnackbar(<FormattedMessage id="manage_users.remove_failed" />, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle row selection
  const handleSelectRow = (userId) => {
    setSelectedRows((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Default Pillars Handlers
  //
  // Names are compared case-insensitively throughout, matching the BE's own
  // tie-break (clean_pillars_def): a pillar must not exist twice under
  // different casing, or the report's pillar axis splits in two.
  const pillarNameTaken = (name, exceptIndex = -1) =>
    defaultPillars.some(
      (p, idx) => idx !== exceptIndex && p.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );

  const persistPillars = (rows) => {
    setDefaultPillars(rows);
    savePillarLibrary(rows);
  };

  const handleAddPillar = () => {
    if (newPillarValue.trim() && !pillarNameTaken(newPillarValue)) {
      persistPillars([
        ...defaultPillars,
        { name: newPillarValue.trim(), definition: newPillarDefinition.trim() },
      ]);
      setNewPillarValue("");
      setNewPillarDefinition("");
      enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillar_added", defaultMessage: "Pillar added successfully" }), {
        variant: "success",
        autoHideDuration: 2000,
      });
    } else if (pillarNameTaken(newPillarValue)) {
      enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillar_exists", defaultMessage: "This pillar already exists" }), {
        variant: "warning",
        autoHideDuration: 2000,
      });
    }
  };

  const handleDeletePillar = (index) => {
    persistPillars(defaultPillars.filter((_, idx) => idx !== index));
    enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillar_removed", defaultMessage: "Pillar removed successfully" }), {
      variant: "success",
      autoHideDuration: 2000,
    });
  };

  const handleStartEditPillar = (index) => {
    setEditingPillarIndex(index);
    setEditingPillarValue(defaultPillars[index].name);
    setEditingPillarDefinition(defaultPillars[index].definition || "");
  };

  const handleSaveEditPillar = () => {
    const current = defaultPillars[editingPillarIndex];
    const nameChanged = editingPillarValue.trim() !== current?.name;
    const definitionChanged = editingPillarDefinition.trim() !== (current?.definition || "");
    if (editingPillarValue.trim() && (nameChanged || definitionChanged)) {
      if (!pillarNameTaken(editingPillarValue, editingPillarIndex)) {
        const updatedPillars = [...defaultPillars];
        updatedPillars[editingPillarIndex] = {
          name: editingPillarValue.trim(),
          definition: editingPillarDefinition.trim(),
        };
        persistPillars(updatedPillars);
        enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillar_updated", defaultMessage: "Pillar updated successfully" }), {
          variant: "success",
          autoHideDuration: 2000,
        });
      } else {
        enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillar_name_exists", defaultMessage: "This pillar name already exists" }), {
          variant: "warning",
          autoHideDuration: 2000,
        });
      }
    }
    setEditingPillarIndex(null);
    setEditingPillarValue("");
    setEditingPillarDefinition("");
  };

  const handleCancelEditPillar = () => {
    setEditingPillarIndex(null);
    setEditingPillarValue("");
    setEditingPillarDefinition("");
  };

  const handleResetPillars = () => {
    setDefaultPillars(resetPillarLibrary());
    enqueueSnackbar(intl.formatMessage({ id: "manage_users.pillars_reset", defaultMessage: "Pillars reset to default values" }), {
      variant: "success",
      autoHideDuration: 2000,
    });
  };

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#f5f5f5", px: 3 }}>
      <Container
        maxWidth={isMobile ? false : "lg"}
        sx={{
          mt: 4,
          px: isMobile ? 2 : 3,
          py: 4,
          textAlign: "center",
          margin: "auto",
        }}
      >
        {/* Add User Form */}
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 3,
            mt: 7,
            mb: 4,
            borderRadius: 2,
            borderLeft: "4px solid #00438E",
            backgroundColor: "#ffffff",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: "#00438E", mb: isMobile ? 1 : 2, fontWeight: "bold" }}>
            <FormattedMessage id="manage_users.add_user" />
          </Typography>
          <Box
            component="form"
            onSubmit={handleAddUserSubmit}
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexDirection: isMobile ? "column" : "row",
              "& > *": { width: isMobile ? "100%" : "auto" },
            }}
          >
            <TextField
              label={<FormattedMessage id="manage_users.first_name" />}
              name="first_name"
              value={newUser.first_name}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                },
              }}
            />
            <TextField
              label={<FormattedMessage id="manage_users.last_name" />}
              name="last_name"
              value={newUser.last_name}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                },
              }}
            />
            <TextField
              label={<FormattedMessage id="manage_users.username" />}
              name="username"
              value={newUser.username}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                },
              }}
            />
            <TextField
              label={<FormattedMessage id="manage_users.email" />}
              name="email"
              value={newUser.email}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": { color: "#00438E" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#00438E" },
                  "&:hover fieldset": { borderColor: "#4A87C8" },
                  "&.Mui-focused fieldset": { borderColor: "#4A87C8" },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              startIcon={<AddIcon sx={{ color: "#ffffff" }} />}
              sx={{
                minWidth: 120,
                mt: isMobile ? 2 : 0,
                backgroundColor: "#00438E",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#003A75" },
                "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" },
              }}
              disabled={loading || !organization.id}
            >
              <FormattedMessage id="manage_users.add" />
            </Button>
          </Box>

          <Dialog open={addUserConfirmOpen} onClose={() => setAddUserConfirmOpen(false)}>
            <DialogTitle>
              <FormattedMessage id="manage_users.add_user_confirm_title" defaultMessage="Send activation email?" />
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <FormattedMessage
                  id="manage_users.add_user_confirm_message"
                  defaultMessage="An activation email will be sent to {email} to set their password and log in to the system. Continue?"
                  values={{ email: newUser.email }}
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddUserConfirmOpen(false)} color="inherit">
                <FormattedMessage id="manage_users.cancel" />
              </Button>
              <Button
                onClick={() => handleAddUser()}
                variant="contained"
                sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
              >
                <FormattedMessage id="manage_users.add" />
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(deleteUserConfirm)} onClose={() => setDeleteUserConfirm(null)}>
            <DialogTitle>
              <FormattedMessage id="manage_users.remove_user_confirm_title" defaultMessage="Remove user?" />
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <FormattedMessage
                  id="manage_users.remove_user_confirm_message"
                  defaultMessage="This user will be removed and will lose access to the website. Continue?"
                  values={deleteUserConfirm ? { username: deleteUserConfirm.username } : {}}
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteUserConfirm(null)} color="inherit">
                <FormattedMessage id="manage_users.cancel" />
              </Button>
              <Button
                onClick={() => {
                  if (deleteUserConfirm) {
                    handleRemoveUsers([deleteUserConfirm.id]);
                    setDeleteUserConfirm(null);
                  }
                }}
                variant="contained"
                color="error"
              >
                <FormattedMessage id="manage_users.remove_confirm" defaultMessage="Remove" />
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>

        {/* Users Table */}
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 1 : 2,
            mb: 4,
            borderRadius: 2,
            borderLeft: "4px solid #00438E",
            backgroundColor: "#ffffff",
            overflowX: isMobile ? "auto" : "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Typography variant="h6" sx={{ color: "#00438E", fontWeight: "bold" }}>
              <FormattedMessage id="manage_users.user_list" />
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon sx={{ color: "#ffffff" }} />}
              onClick={handleRemoveUsers}
              disabled={loading || selectedRows.length === 0}
              sx={{
                mt: isMobile ? 2 : 0,
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#b71c1c" },
                "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "rgba(0, 0, 0, 0.38)" },
              }}
            >
              <FormattedMessage id="manage_users.remove_selected" />
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress sx={{ color: "#00438E" }} />
            </Box>
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: "#ffebee",
                color: "#d32f2f",
                "& .MuiAlert-icon": { color: "#d32f2f" },
              }}
            >
              {error}
            </Alert>
          ) : orgUsers.length === 0 ? (
            <Typography variant="body1" sx={{ p: 2, textAlign: "center", color: "textSecondary" }}>
              <FormattedMessage id="manage_users.no_users_found" />
            </Typography>
          ) : (
            <Box sx={{ overflowX: isMobile ? "auto" : "visible" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedRows.length > 0 && selectedRows.length < orgUsers.length}
                        checked={orgUsers.length > 0 && selectedRows.length === orgUsers.length}
                        onChange={(e) =>
                          setSelectedRows(e.target.checked ? orgUsers.map((u) => u.id) : [])
                        }
                        sx={{
                          color: "#00438E",
                          "&.Mui-checked": { color: "#00438E" },
                          "&.MuiCheckbox-indeterminate": { color: "#00438E" },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#00438E", fontWeight: "bold" }}>
                      <FormattedMessage id="manage_users.first_name" />
                    </TableCell>
                    <TableCell sx={{ color: "#00438E", fontWeight: "bold" }}>
                      <FormattedMessage id="manage_users.last_name" />
                    </TableCell>
                    <TableCell sx={{ color: "#00438E", fontWeight: "bold" }}>
                      <FormattedMessage id="manage_users.username" />
                    </TableCell>
                    <TableCell sx={{ color: "#00438E", fontWeight: "bold" }}>
                      <FormattedMessage id="manage_users.email" />
                    </TableCell>
                    <TableCell sx={{ color: "#00438E", fontWeight: "bold" }}>
                      <FormattedMessage id="manage_users.actions" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(user.id)}
                          onChange={() => handleSelectRow(user.id)}
                          sx={{
                            color: "#00438E",
                            "&.Mui-checked": { color: "#00438E" },
                          }}
                        />
                      </TableCell>
                      <TableCell>{user.first_name}</TableCell>
                      <TableCell>{user.last_name}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => setDeleteUserConfirm({ id: user.id, username: user.username })}
                          aria-label="delete"
                          disabled={loading}
                          sx={{
                            p: isMobile ? 0.5 : 1,
                            color: "#d32f2f",
                            "&:hover": { color: "#b71c1c" },
                            "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.38)" },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>

        {/* Default Pillars Management */}
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 3,
            mb: 4,
            borderRadius: 2,
            borderLeft: "4px solid #ed6c02",
            backgroundColor: "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <SettingsIcon sx={{ color: "#ed6c02" }} />
            <Typography variant="h6" sx={{ color: "#ed6c02", fontWeight: "bold" }}>
              <FormattedMessage id="manage_users.default_pillars" defaultMessage="Default Pillars" />
            </Typography>
          </Box>

          {/* Add New Pillar */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              <FormattedMessage
                id="manage_users.pillar_definition_hint"
                defaultMessage="The definition tells report generation what each pillar covers — it decides which pillar every question is assigned to, so say what a pillar excludes as well as what it includes."
              />
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexDirection: isMobile ? "column" : "row" }}>
              <TextField
                size="small"
                placeholder={intl.formatMessage({ id: "manage_users.enter_pillar_name", defaultMessage: "Enter pillar name..." })}
                value={newPillarValue}
                onChange={(e) => setNewPillarValue(e.target.value)}
                sx={{ width: isMobile ? "100%" : 240, flexShrink: 0 }}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder={intl.formatMessage({ id: "manage_users.enter_pillar_definition", defaultMessage: "What this pillar covers..." })}
                value={newPillarDefinition}
                onChange={(e) => setNewPillarDefinition(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleAddPillar}
                startIcon={<AddIcon />}
                disabled={!newPillarValue.trim() || pillarNameTaken(newPillarValue)}
                sx={{
                  minWidth: isMobile ? "100%" : "150px",
                  backgroundColor: "#2e7d32",
                  "&:hover": {
                    backgroundColor: "#1b5e20",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "rgba(0, 0, 0, 0.38)",
                  },
                }}
              >
                <FormattedMessage id="manage_users.add_pillar" defaultMessage="Add Pillar" />
              </Button>
            </Box>
          </Box>

          {/* Pillars List */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333" }}>
              <FormattedMessage id="manage_users.all_pillars_count" defaultMessage="All Pillars ({count})" values={{ count: defaultPillars.length }} />
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetPillars}
              sx={{
                borderColor: "#ed6c02",
                color: "#ed6c02",
                "&:hover": {
                  borderColor: "#f57c00",
                  backgroundColor: "rgba(237, 108, 2, 0.04)",
                },
              }}
            >
              <FormattedMessage id="manage_users.reset_to_default" defaultMessage="Reset to Default" />
            </Button>
          </Box>

          <List
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              maxHeight: "400px",
              overflow: "auto",
              backgroundColor: "#fafafa",
            }}
          >
            {defaultPillars.map((pillar, idx) => {
              const isEditing = editingPillarIndex === idx;

              return (
                <React.Fragment key={idx}>
                  <ListItem
                    sx={{
                      backgroundColor: "white",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    {isEditing ? (
                      <Box sx={{ display: "flex", gap: 1, width: "100%", alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>
                        <TextField
                          size="small"
                          value={editingPillarValue}
                          onChange={(e) => setEditingPillarValue(e.target.value)}
                          autoFocus
                          sx={{ width: isMobile ? "100%" : 220, flexShrink: 0 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          placeholder={intl.formatMessage({ id: "manage_users.enter_pillar_definition", defaultMessage: "What this pillar covers..." })}
                          value={editingPillarDefinition}
                          onChange={(e) => setEditingPillarDefinition(e.target.value)}
                        />
                        <Box sx={{ display: "flex", gap: 1, minWidth: isMobile ? "100%" : "auto" }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveEditPillar}
                            sx={{ minWidth: "60px", flex: isMobile ? 1 : "none" }}
                          >
                            <FormattedMessage id="manage_users.save" defaultMessage="Save" />
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelEditPillar}
                            sx={{ minWidth: "60px", flex: isMobile ? 1 : "none" }}
                          >
                            <FormattedMessage id="manage_users.cancel" defaultMessage="Cancel" />
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <ListItemText
                          primary={pillar.name}
                          secondary={
                            pillar.definition
                              || intl.formatMessage({
                                id: "manage_users.pillar_no_definition",
                                defaultMessage: "No definition yet — report generation will only have the name to go on.",
                              })
                          }
                          primaryTypographyProps={{ fontWeight: 500, color: "#333" }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              color: pillar.definition ? "text.secondary" : "warning.main",
                              fontStyle: pillar.definition ? "normal" : "italic",
                              pr: 8,
                            },
                          }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleStartEditPillar(idx)}
                            sx={{
                              mr: 0.5,
                              color: "#1976d2",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.1)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeletePillar(idx)}
                            sx={{
                              color: "#d32f2f",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </>
                    )}
                  </ListItem>
                  {idx < defaultPillars.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>

        </Paper>
      </Container>
    </Box>
  );
}